import { createAuthClient } from "better-auth/react";
import { magicLinkClient, jwtClient } from "better-auth/client/plugins";

// Empty baseURL makes all auth requests relative (e.g. /api/auth/...).
// In dev, Vite proxies /api to localhost:3001; in production, same origin.
export const authClient = createAuthClient({
  baseURL: "",
  plugins: [magicLinkClient(), jwtClient()],
});

// Helper: get a short-lived JWT access token for the Neon Data API.
// The JWT contains `sub` (user ID) which Neon Data API extracts for RLS.
// Caches the token and only refreshes when near expiry (< 2 min remaining).
let cachedToken: string | null = null;
let tokenExpiresAt = 0;
let inflightRequest: Promise<string | null> | null = null;
export const TOKEN_STORAGE_KEY = "production-hub.auth-token";

type StoredAccessToken = {
  token: string;
  expiresAt: number;
};

export type AccessTokenDebugInfo = {
  present: boolean;
  expiresAt: number | null;
  issuedAt: number | null;
  expiresInMs: number | null;
  source: "memory" | "storage" | "none";
};

function readStoredAccessToken(): StoredAccessToken | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    return parseStoredAccessToken(raw);
  } catch {
    return null;
  }
}

export function parseStoredAccessToken(
  raw: string | null,
): StoredAccessToken | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StoredAccessToken;
    if (!parsed.token || !parsed.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredAccessToken(token: string, expiresAt: number) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    TOKEN_STORAGE_KEY,
    JSON.stringify({ token, expiresAt } satisfies StoredAccessToken),
  );
}

function clearStoredAccessToken() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
}

function decodeTokenExpiresAt(token: string, fallback: number) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000;
  } catch {
    return fallback;
  }
}

function decodeTokenIssuedAt(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.iat === "number" ? payload.iat * 1000 : null;
  } catch {
    return null;
  }
}

function isBrowserOffline() {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function getAccessToken(): Promise<string | null> {
  const now = Date.now();
  const minRemaining = isBrowserOffline() ? 0 : 2 * 60 * 1000;

  if (cachedToken && tokenExpiresAt - now > minRemaining) {
    return cachedToken;
  }

  const stored = readStoredAccessToken();
  if (stored && stored.expiresAt - now > minRemaining) {
    cachedToken = stored.token;
    tokenExpiresAt = stored.expiresAt;
    return cachedToken;
  }

  return fetchFreshAccessToken();
}

// Always hits the network (modulo in-flight dedup) — used by the token
// manager when it needs to proactively refresh or recover from `needs-auth`.
export async function fetchFreshAccessToken(): Promise<string | null> {
  if (inflightRequest) return inflightRequest;
  inflightRequest = (async () => {
    const retryDelays = isBrowserOffline() ? [0] : [0, 100, 250, 500, 1000];
    let lastError: unknown;
    try {
      for (const retryDelay of retryDelays) {
        if (retryDelay > 0) await delay(retryDelay);
        try {
          const { data } = await authClient.token();
          cachedToken = data?.token ?? null;
          if (cachedToken) {
            tokenExpiresAt = decodeTokenExpiresAt(
              cachedToken,
              Date.now() + 10 * 60 * 1000,
            );
            writeStoredAccessToken(cachedToken, tokenExpiresAt);
          }
          return cachedToken;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError;
    } catch (error) {
      const fallbackToken = readStoredAccessToken();
      if (fallbackToken && fallbackToken.expiresAt > Date.now()) {
        cachedToken = fallbackToken.token;
        tokenExpiresAt = fallbackToken.expiresAt;
        return cachedToken;
      }
      throw error;
    } finally {
      inflightRequest = null;
    }
  })();
  return inflightRequest;
}

export function clearAccessToken() {
  cachedToken = null;
  tokenExpiresAt = 0;
  clearStoredAccessToken();
}

export function setCachedAccessToken(token: string, expiresAt: number) {
  cachedToken = token;
  tokenExpiresAt = expiresAt;
}

export function getCachedAccessToken(): string | null {
  const now = Date.now();
  if (cachedToken && tokenExpiresAt > now) {
    return cachedToken;
  }

  const stored = readStoredAccessToken();
  if (stored && stored.expiresAt > now) {
    cachedToken = stored.token;
    tokenExpiresAt = stored.expiresAt;
    return cachedToken;
  }

  return null;
}

export function hasValidStoredAccessToken(): boolean {
  return getCachedAccessToken() !== null;
}

// Returns the last-known token regardless of expiry. Used for offline
// startup so that Zero can mount and serve cached IndexedDB data even
// when we can't reach the auth server to refresh.
export function getStoredAccessTokenIgnoringExpiry(): string | null {
  if (cachedToken) return cachedToken;
  return readStoredAccessToken()?.token ?? null;
}

export function getAccessTokenDebugInfo(): AccessTokenDebugInfo {
  const now = Date.now();
  if (cachedToken && tokenExpiresAt > now) {
    return {
      present: true,
      expiresAt: tokenExpiresAt,
      issuedAt: decodeTokenIssuedAt(cachedToken),
      expiresInMs: tokenExpiresAt - now,
      source: "memory",
    };
  }

  const stored = readStoredAccessToken();
  if (stored && stored.expiresAt > now) {
    return {
      present: true,
      expiresAt: stored.expiresAt,
      issuedAt: decodeTokenIssuedAt(stored.token),
      expiresInMs: stored.expiresAt - now,
      source: "storage",
    };
  }

  return {
    present: false,
    expiresAt: null,
    issuedAt: null,
    expiresInMs: null,
    source: "none",
  };
}
